/**
 * TanStack Query client configuration.
 *
 * Provides a pre-configured QueryClient with:
 * - Default stale times (5 minutes for most data)
 * - Retry logic (3 retries with exponential backoff)
 * - Error handling configuration
 * - Window focus refetching behavior
 */

import { QueryClient } from '@tanstack/react-query';
import { getActiveOrgId } from '@/features/orgs/workspaceStore';

/**
 * Default stale time in milliseconds (5 minutes).
 * Data is considered fresh for this duration and won't be refetched
 * on component remounts.
 */
export const DEFAULT_STALE_TIME = 5 * 60 * 1000;

/**
 * Default garbage collection time in milliseconds (10 minutes).
 * Unused cache entries are removed after this duration.
 */
export const DEFAULT_GC_TIME = 10 * 60 * 1000;

/**
 * Create a configured QueryClient instance.
 *
 * Configuration:
 * - Queries:
 *   - 5 minute stale time (data considered fresh)
 *   - 10 minute garbage collection time
 *   - 3 retries with exponential backoff
 *   - Refetch on window focus enabled
 *   - Retry on 4xx errors disabled (client errors shouldn't retry)
 *
 * - Mutations:
 *   - No automatic retries (manual retry preferred)
 *   - Error handling via onError callbacks
 */
export function createQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        // Data freshness
        staleTime: DEFAULT_STALE_TIME,
        gcTime: DEFAULT_GC_TIME,

        // Retry configuration: 3 retries with exponential backoff
        retry: (failureCount, error) => {
          // Don't retry if we've failed 3 times
          if (failureCount >= 3) return false;

          // Don't retry on 4xx errors (client errors)
          if (error instanceof Error) {
            const status = (error as Error & { status?: number }).status;
            if (status && status >= 400 && status < 500) {
              return false;
            }
          }

          return true;
        },
        retryDelay: (attemptIndex) => {
          // Exponential backoff: 1s, 2s, 4s
          return Math.min(1000 * 2 ** attemptIndex, 30000);
        },

        // Refetch behavior
        refetchOnWindowFocus: true,
        refetchOnReconnect: true,
        refetchOnMount: true,
      },
      mutations: {
        // No automatic retries for mutations
        retry: false,
      },
    },
  });
}

/**
 * Query keys factory for consistent cache key management.
 *
 * Usage:
 * ```typescript
 * // In a query hook
 * useQuery({
 *   queryKey: queryKeys.chats.list({ archived: false }),
 *   queryFn: () => fetchChats({ archived: false }),
 * });
 *
 * // Invalidating queries
 * queryClient.invalidateQueries({ queryKey: queryKeys.chats.all });
 * ```
 */
export const queryKeys = {
  // Chat queries
  chats: {
    all: ['chats'] as const,
    // Andi for Business: in a business workspace every chat key carries the org id, so a
    // cached personal list or chat can never be served in a business workspace (and the
    // reverse). Personal keys are exactly what they always were.
    scope: () => {
      const orgId = getActiveOrgId();
      return orgId ? ([...queryKeys.chats.all, 'org', orgId] as const) : queryKeys.chats.all;
    },
    lists: () => [...queryKeys.chats.scope(), 'list'] as const,
    list: (filters: Record<string, unknown>) => [...queryKeys.chats.lists(), filters] as const,
    details: () => [...queryKeys.chats.scope(), 'detail'] as const,
    detail: (id: string) => [...queryKeys.chats.details(), id] as const,
    messages: (chatId: string) => [...queryKeys.chats.detail(chatId), 'messages'] as const,
    shared: (shareId: string) => ['shared-chat', shareId] as const,
    tags: (chatId: string) => [...queryKeys.chats.detail(chatId), 'tags'] as const,
    allTags: () => [...queryKeys.chats.scope(), 'all-tags'] as const,
  },

  // File queries
  files: {
    all: ['files'] as const,
    lists: () => [...queryKeys.files.all, 'list'] as const,
    list: (filters: Record<string, unknown>) => [...queryKeys.files.lists(), filters] as const,
    details: () => [...queryKeys.files.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.files.details(), id] as const,
  },

  // Knowledge base queries
  knowledge: {
    all: ['knowledge'] as const,
    lists: () => [...queryKeys.knowledge.all, 'list'] as const,
    list: (filters: Record<string, unknown>) => [...queryKeys.knowledge.lists(), filters] as const,
    details: () => [...queryKeys.knowledge.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.knowledge.details(), id] as const,
    files: (kbId: string) => [...queryKeys.knowledge.detail(kbId), 'files'] as const,
  },

  // Folder queries
  folders: {
    all: ['folders'] as const,
    lists: () => [...queryKeys.folders.all, 'list'] as const,
    list: (filters: Record<string, unknown>) => [...queryKeys.folders.lists(), filters] as const,
    tree: () => [...queryKeys.folders.all, 'tree'] as const,
    details: () => [...queryKeys.folders.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.folders.details(), id] as const,
  },

  // User queries
  user: {
    current: ['user', 'current'] as const,
    settings: ['user', 'settings'] as const,
    /** App wide preferences stored by web-backend (`/users/me/preferences`). */
    preferences: ['user', 'preferences'] as const,
    /** The user's custom Andi look (`/users/me/avatar`). */
    avatar: ['user', 'avatar'] as const,
    /** How Andi talks to the user (`/users/me/response-style`). */
    responseStyle: ['user', 'responseStyle'] as const,
  },

  /** The user's main chat (`GET /main-thread`). */
  mainThread: ['main-thread'] as const,

  // API key queries
  apiKeys: {
    all: ['api-keys'] as const,
    list: () => [...queryKeys.apiKeys.all, 'list'] as const,
  },

  // Library queries (Plan: Library feature)
  library: {
    all: ['library'] as const,
    home: () => [...queryKeys.library.all, 'home'] as const,
    items: (filters: Record<string, unknown>) =>
      [...queryKeys.library.all, 'items', filters] as const,
    detail: (id: string) => [...queryKeys.library.all, 'detail', id] as const,
    revisions: (id: string) => [...queryKeys.library.detail(id), 'revisions'] as const,
    activity: (id: string) => [...queryKeys.library.detail(id), 'activity'] as const,
    collections: () => [...queryKeys.library.all, 'collections'] as const,
  },

  /**
   * Deliberately OUTSIDE the `['library']` prefix (finding 7, plan 2.6/5.6):
   * live data is fetched only on open, on Refresh and on Try again, and
   * MUST NOT be refetched by any `library.all`/`library.detail(id)`
   * invalidation (pin, rename, move to collection, collection CRUD, every
   * `library_assets` stream frame). Nesting it under `library` would make
   * every one of those prefix-match invalidations refetch it, spending a
   * Plaid read (and the per-user rate limit) on actions that never change
   * live data.
   */
  libraryLive: {
    data: (id: string) => ['library-live', id] as const,
  },
} as const;

/**
 * Pre-created QueryClient instance for the application.
 * This should be used in the QueryClientProvider.
 */
export const queryClient = createQueryClient();
